import React, { useMemo, useState } from "react";
import { ArrowRight, Loader2, Clock3, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAdTracking } from "../utils/tracking";
import "./InlineLeadForm.css";

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_CODES = [
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+27", label: "🇿🇦 +27" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Inline lead-capture form (Google-Ads-optimised).
 *
 * On successful submit:
 *   1. POSTs to /api/landing/leads
 *   2. Fires window.gtag_report_conversion()
 *   3. Navigates to /start with the visitor's details in query params
 *      so the /start page can render a personalised WhatsApp CTA.
 */
export default function InlineLeadForm({
  source = "landing_cta",
  variant = "hero",
  headline,
  subheadline,
  ctaText = "Reserve My Seat",
}) {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(
    () =>
      firstName.trim().length >= 1 &&
      whatsapp.replace(/\D/g, "").length >= 6 &&
      EMAIL_RE.test(email.trim()) &&
      targetRole.trim().length >= 1 &&
      !submitting,
    [firstName, whatsapp, email, targetRole, submitting]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (whatsapp.replace(/\D/g, "").length < 6) {
      setError("Please enter a valid phone number.");
      return;
    }
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const cleanWa = whatsapp.replace(/\D/g, "");
      const cleanEmail = email.trim();
      const r = await fetch(`${API}/api/landing/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          country_code: countryCode,
          whatsapp: cleanWa,
          email: cleanEmail,
          target_role: targetRole.trim(),
          source,
          ...getAdTracking(),
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data.detail || "Couldn't save your details. Please try again.");
      }

      // Fire Google Ads conversion tracking (form-submit is the primary conversion)
      if (typeof window.gtag_report_conversion === "function") {
        try { window.gtag_report_conversion(); } catch (_) {}
      }
      if (typeof window.gtag === "function") {
        try {
          window.gtag("event", "generate_lead", {
            event_category: "lead",
            event_label: source,
            value: 1,
          });
        } catch (_) {}
      }

      const params = new URLSearchParams({
        name: firstName.trim(),
        role: targetRole.trim(),
        cc: countryCode,
        wa: cleanWa,
        email: cleanEmail,
        src: source,
      });
      navigate(`/start?${params.toString()}`);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`ilf-card ilf-${variant}`}
      id={variant === "hero" ? "lead-form" : undefined}
      data-testid={`inline-lead-form-${variant}`}
    >
      <span className="ilf-eyebrow" data-testid="ilf-eyebrow">
        <span className="ilf-eyebrow-dot" />
        Free personal plan · Reply in 15 min
      </span>
      <h3 className="ilf-title" data-testid="ilf-title">
        {headline || "Get your free job-search plan"}
      </h3>
      <p className="ilf-sub" data-testid="ilf-sub">
        {subheadline || "Tell us your target role. Our team messages you within 15 minutes with your personal plan. Free. No card, no sales call."}
      </p>

      <form onSubmit={submit} data-testid="ilf-form">
        <div className="ilf-field">
          <label className="ilf-label" htmlFor={`ilf-name-${variant}`}>Your first name</label>
          <input
            id={`ilf-name-${variant}`}
            className="ilf-input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="e.g., Priya"
            autoComplete="given-name"
            data-testid="ilf-input-name"
            required
            maxLength={100}
          />
        </div>

        <div className="ilf-field">
          <label className="ilf-label" htmlFor={`ilf-role-${variant}`}>Target role you want</label>
          <input
            id={`ilf-role-${variant}`}
            className="ilf-input"
            value={targetRole}
            onChange={(e) => setTargetRole(e.target.value)}
            placeholder="e.g., Senior Data Engineer, USA"
            autoComplete="off"
            data-testid="ilf-input-role"
            required
            maxLength={200}
          />
        </div>

        <div className="ilf-field">
          <label className="ilf-label" htmlFor={`ilf-wa-${variant}`}>WhatsApp number</label>
          <div className="ilf-tel-row">
            <select
              className="ilf-input ilf-cc"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              data-testid="ilf-input-cc"
              aria-label="Country code"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            <input
              id={`ilf-wa-${variant}`}
              className="ilf-input"
              inputMode="numeric"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="10-digit number"
              autoComplete="tel-national"
              data-testid="ilf-input-whatsapp"
              required
            />
          </div>
        </div>

        <div className="ilf-field">
          <label className="ilf-label" htmlFor={`ilf-email-${variant}`}>Email address</label>
          <input
            id={`ilf-email-${variant}`}
            className="ilf-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            data-testid="ilf-input-email"
            required
            maxLength={200}
          />
        </div>

        {error && (
          <div className="ilf-error" data-testid="ilf-error">{error}</div>
        )}

        <button
          type="submit"
          className="ilf-submit"
          disabled={submitting}
          data-testid="ilf-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              {ctaText}
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <div className="ilf-micro" data-testid="ilf-micro">
          Takes 20 seconds · No spam ever
        </div>
      </form>

      <div className="ilf-trust" data-testid="ilf-trust">
        <div className="ilf-trust-item">
          <div className="ilf-avatars" aria-hidden="true">
            <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=50&q=80&auto=format&fit=crop" alt="" width="24" height="24" loading="lazy" />
            <img src="https://plus.unsplash.com/premium_photo-1682089810582-f7b200217b67?w=50&q=80&auto=format&fit=crop" alt="" width="24" height="24" loading="lazy" />
            <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop" alt="" width="24" height="24" loading="lazy" />
            <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop" alt="" width="24" height="24" loading="lazy" />
          </div>
          <div className="ilf-stars" aria-label="4.9 star rating">
            {[0,1,2,3,4].map((i) => (
              <Star key={i} className="w-3.5 h-3.5" fill="currentColor" />
            ))}
          </div>
          <span><strong>4.9/5</strong> · 800+ clients hired</span>
        </div>
        <div className="ilf-trust-item">
          <Clock3 className="w-4 h-4" />
          <span>Personal reply <strong>under 15 min</strong></span>
        </div>
      </div>
    </div>
  );
}
